import { graphql, useFragment } from 'react-relay';

import {
  BrandMessageExecutionConnectionDataTable_brandMessageExecutionConnection$data,
  BrandMessageExecutionConnectionDataTable_brandMessageExecutionConnection$key,
} from '../../../relay/__generated__/BrandMessageExecutionConnectionDataTable_brandMessageExecutionConnection.graphql';
import { numberWithCommas } from '../../../utils/number';
import Button from '../../core/Button';
import DataTable, { DataTableProps } from '../../core/DataTable';
import DateText from '../../core/DateText';
import DialogHandler from '../../core/DialogHandler';
import EnumPair from '../../core/EnumPair';
import Text from '../../core/Text';
import Tooltip from '../../core/Tooltip';
import View from '../../core/View';
import BrandMessageSendFailedDialog from '../BrandMessageSendFailedDialog';

/** 전달 성공분만 과금된다. 친구 18원 / 비친구 23원 × 1.1(VAT) */
const UNIT_COST = { friend: 19.8, normal: 25.3 };

/** 결과가 아직 안 들어왔으면 0 이 아니라 비어 있는 상태로 봐야 한다. */
const hasResult = (friend: number | null, normal: number | null) => friend !== null || normal !== null;

const Countline = ({ label, count, total }: { label: string; count: number; total: number }) => (
  <Text sx={{ display: 'block', fontSize: 0, color: 'fg.muted' }}>
    {`${label} ${numberWithCommas(count)}`}
    {total > 0 ? ` (${((count / total) * 100).toFixed(2)}%)` : ''}
  </Text>
);

export const BrandMessageExecutionConnectionDataTable_brandMessageExecutionConnection = graphql`
  fragment BrandMessageExecutionConnectionDataTable_brandMessageExecutionConnection on BrandMessageExecutionConnection {
    edges {
      node {
        id
        sequence
        created
        executedAt
        queriedAt
        status
        targetCount
        deliveredFriendCount
        deliveredNormalCount
        deliveredCount
        failedCount
        sendFailedCount
        sendFailedPhoneNumbers
        sendFailedVariables
        task {
          id
        }
      }
    }
  }
`;

type BrandMessageExecutionNode =
  BrandMessageExecutionConnectionDataTable_brandMessageExecutionConnection$data['edges'][number]['node'];

type Props = {
  brandMessageExecutionConnection: BrandMessageExecutionConnectionDataTable_brandMessageExecutionConnection$key;
} & Omit<DataTableProps<BrandMessageExecutionNode>, 'rows' | 'columns'>;

const BrandMessageExecutionConnectionDataTable = ({ brandMessageExecutionConnection, ...props }: Props) => {
  const { edges } = useFragment(
    BrandMessageExecutionConnectionDataTable_brandMessageExecutionConnection,
    brandMessageExecutionConnection,
  );

  const rows = edges.map(({ node }) => node).filter((node): node is BrandMessageExecutionNode => Boolean(node));

  return (
    <DataTable
      rows={rows}
      columns={[
        {
          field: 'created',
          title: '생성일시',
          renderValue: ({ created }) => <DateText>{created}</DateText>,
          width: 150,
        },
        {
          field: 'executedAt',
          title: '예약일시',
          renderValue: ({ executedAt }) => (executedAt ? <DateText>{executedAt}</DateText> : '-'),
          width: 150,
        },
        {
          field: 'status',
          title: '실행상태',
          renderValue: ({ status }) => <EnumPair typename={'BrandMessageExecutionStatusEnum'}>{status}</EnumPair>,
          width: 100,
        },
        {
          field: 'targetCount',
          title: '발송 대상',
          renderValue: ({ targetCount }) => <Text>{numberWithCommas(targetCount)}</Text>,
          width: 120,
        },
        {
          field: 'cost',
          title: '비용',
          // 단가가 바뀌어도 서버 배포 없이 대응할 수 있게 여기서 계산한다.
          renderValue: ({ deliveredFriendCount, deliveredNormalCount }) =>
            hasResult(deliveredFriendCount, deliveredNormalCount) ? (
              <Text sx={{ fontWeight: 'bold', color: 'success.fg' }}>
                {numberWithCommas(
                  Math.round(
                    (deliveredFriendCount ?? 0) * UNIT_COST.friend + (deliveredNormalCount ?? 0) * UNIT_COST.normal,
                  ),
                )}
              </Text>
            ) : (
              <Text>{'-'}</Text>
            ),
          width: 110,
          align: 'end',
        },
        {
          field: 'remark',
          title: (
            <Tooltip text={'실패는 전체 시도 대비, 친구·비친구는 전달 성공 대비'} direction={'s'}>
              <Text>{'비고'}</Text>
            </Tooltip>
          ),
          renderValue: ({ deliveredFriendCount, deliveredNormalCount, deliveredCount, failedCount, targetCount }) => {
            if (!hasResult(deliveredFriendCount, deliveredNormalCount)) return <Text>{'-'}</Text>;
            const delivered = deliveredCount ?? 0;
            return (
              <View>
                {/* 실패는 전체 시도 대비, 친구/비친구는 전달 성공 대비 — 메시지 플래너와 같은 기준 */}
                <Countline label={'실패'} count={failedCount ?? 0} total={targetCount} />
                <Countline label={'친구'} count={deliveredFriendCount ?? 0} total={delivered} />
                <Countline label={'비친구'} count={deliveredNormalCount ?? 0} total={delivered} />
              </View>
            );
          },
          width: 190,
        },
        {
          field: 'sendFailedCount',
          title: (
            <Tooltip
              text={'비즈뿌리오 발송 접수 미확인 건입니다. 콘솔 확인 후 미접수 건만 재발송해 주세요.'}
              direction={'s'}
            >
              <Text>{'발송 접수 미확인'}</Text>
            </Tooltip>
          ),
          // 뿌리오에 접수조차 되지 않아 나가지 않은 건. 전달 실패(카카오 거절 등)와 달리 다시 보내면 된다.
          renderValue: ({ sendFailedCount, sendFailedPhoneNumbers, sendFailedVariables }) =>
            sendFailedCount > 0 ? (
              <DialogHandler
                renderDialog={({ isOpen, closeDialog }) => (
                  <BrandMessageSendFailedDialog
                    isOpen={isOpen}
                    onDismiss={closeDialog}
                    wide
                    phoneNumbers={sendFailedPhoneNumbers}
                    variables={sendFailedVariables as Record<string, string>[]}
                  />
                )}
              >
                <Button size={'small'} variant={'plain'}>
                  {`${numberWithCommas(sendFailedCount)}건`}
                </Button>
              </DialogHandler>
            ) : (
              <Text>{'-'}</Text>
            ),
          width: 160,
        },
      ]}
      {...props}
    />
  );
};

export default BrandMessageExecutionConnectionDataTable;
